import React, { useEffect, useState } from 'react'
import SocialMedia from '../../component/SocialMedia/socialMedia'
import { FaReact } from 'react-icons/fa'
import highlightKeywords from '../../data/highlightKeywords' 
import workHistory from '../../data/workHistory' 

function About() {
  const [scrollY, setScrollY] = useState(0)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const hobbies = ['Chess', 'Hiking', 'Reading', 'Board games', 'Game development', 'Data visualization', 'Cooking', 'Photography', 'Portuguese']

  return (
    <div style={{ fontFamily: "'Roboto', sans-serif", margin: 0, padding: 0 }}>
      {/* Hero */}
      <section style={{ height: '60vh', minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', padding: '0 20px', opacity: scrollY < 80 ? 1 : 0.95, transition: 'opacity 0.6s ease' }}>
        <div>
          <FaReact style={{ fontSize: 56, marginBottom: 12, opacity: 0.95 }} />
          <h1 style={{ fontSize: '44px', fontWeight: 700, margin: '6px 0 12px', transform: `translateY(${scrollY * 0.06}px)`, transition: 'transform 0.3s ease-out' }}>About Me</h1>
          <p style={{ fontSize: '20px', maxWidth: 760, margin: '0 auto', lineHeight: 1.6 }}>Full-stack developer passionate about crafting accessible, performant user experiences. Specialized in React, Angular, and Flutter, with a strong emphasis on modern tooling, thoughtful design, and scalable application architecture.</p>
        </div>
      </section>

      {/* Content Sections */}
      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: '80px 20px' }}>
        <section style={{ marginBottom: '60px', opacity: scrollY > 50 ? 1 : 0, transform: scrollY > 50 ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out' }}>
          <h2 style={{ fontSize: '28px', borderBottom: '3px solid #667eea', display: 'inline-block', paddingBottom: '6px', color: '#333', marginBottom: '14px' }}>My Story</h2>
          <p style={{ fontSize: '18px', color: '#555', lineHeight: 1.7, marginTop: 12 }}>As a young kid, the earliest apps were simple, but incredibly fun. One of the most memorable was Flappy Bird—a straightforward platforming game that brought people together as they competed for the highest score. Watching something so simple create excitement and connection left a lasting impression on me.</p>

          <p style={{ fontSize: '18px', color: '#555', lineHeight: 1.7, marginTop: 12 }}>That experience sparked an important realization. While computer science isn’t life-saving in the way a doctor’s work might be, its impact is far-reaching and infinitely deep. Software has the power to connect people, influence behavior, and scale ideas far beyond a single moment or place.</p>

          <p style={{ fontSize: '18px', color: '#555', lineHeight: 1.7, marginTop: 12 }}>With that reach in mind, I knew I wanted to build things that help people and bring them closer together—whether through games that create shared experiences or applications that empower people to better understand and monitor the systems that affect their lives.</p>
        </section>

        <section style={{ marginBottom: '60px', opacity: scrollY > 200 ? 1 : 0, transform: scrollY > 200 ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out' }}>
          <h2 style={{ fontSize: '28px', borderBottom: '3px solid #667eea', display: 'inline-block', paddingBottom: '6px', color: '#333', marginBottom: '16px' }}>Hobbies & Interests</h2>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {hobbies.map((h) => (
              <div key={h} style={{ background: '#f5f7ff', color: '#222', padding: '10px 14px', borderRadius: '999px', fontWeight: 600, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>{h}</div>
            ))}
          </div>
        </section>

        <section style={{ marginBottom: '60px', opacity: scrollY > 400 ? 1 : 0, transform: scrollY > 400 ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out' }}>
          <h2 style={{ fontSize: '28px', borderBottom: '3px solid #667eea', display: 'inline-block', paddingBottom: '6px', color: '#333', marginBottom: '16px' }}>Experience</h2>
          <div style={{ display: 'grid', gap: '18px' }}>
            {workHistory.map((w) => (
              <div key={w.id} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12, alignItems: 'start', padding: 14, background: '#fff', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.05)' }}>
                <div style={{ color: '#667eea', fontWeight: 700 }}>{w.period}</div>
                <div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    {w.companyLogo && (
                      <img src={w.companyLogo} alt={`${w.company} logo`} style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 6 }} />
                    )}
                    <div>
                      <h3 style={{ margin: '0 0 6px' }}>{w.role} <span style={{ color: '#555', fontWeight: 600 }}>— {w.company}</span></h3>
                      <p style={{ margin: 0, color: '#555' }}>{w.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={{ marginBottom: '40px', opacity: scrollY > 350 ? 1 : 0, transform: scrollY > 350 ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out' }}>
          <h2 style={{ fontSize: '28px', borderBottom: '3px solid #667eea', display: 'inline-block', paddingBottom: '6px', color: '#333', marginBottom: '16px' }}>Philosophy & Approach</h2>
          <div style={{ background: '#fff', padding: 16, borderRadius: 10, boxShadow: '0 6px 18px rgba(0,0,0,0.06)' }}>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#555', lineHeight: 1.7 }}>
              <li style={{ marginBottom: 8 }}>Prioritize accessibility, performance, and maintainability.</li>
              <li style={{ marginBottom: 8 }}>I write code that’s clean, readable, and maintainable—so it’s easy for others to understand and build upon.</li>
              <li style={{ marginBottom: 8 }}>Favor iterative, evidence-driven design: ship small improvements, measure impact, and refine based on user feedback.</li>
              <li style={{ marginBottom: 8 }}>I build software with the user and team in mind, creating solutions that are both useful and enjoyable.</li>
              <li style={{ marginBottom: 8 }}>Emphasize collaboration, clear communication, and automated testing to build reliable, scalable products.</li>
            <li style={{ marginBottom: 8 }}>I believe that people often overlook what’s essential until it’s missing. I code with this principle in mind, focusing on usability, performance, and creating solutions that truly help people.</li>
            </ul>
          </div>
        </section>

        <section style={{ textAlign: 'center', marginTop: '40px' }}>
          <h2 style={{ fontSize: '28px', borderBottom: '3px solid #667eea', display: 'inline-block', paddingBottom: '6px', color: '#333', marginBottom: '18px' }}>Contact</h2>
          <SocialMedia links={[{ label: 'LinkedIn', url: 'https://linkedin.com/' }, { label: 'GitHub', url: 'https://github.com/' }]} size={44} />
        </section>
      </main>

    </div>
  )
}

export default About
